
import { Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LoginService } from '@shared-angular/services/login.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ProjectImportApiResponse, ProjectImportFailureReport } from '@shared-angular/services/project-import.types';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { SelectModule } from 'primeng/select';
import { ProjectService } from 'src/app/shared/services/project.service';

@Component({
  selector: 'app-import-project-modal',
  imports: [FormsModule, SelectModule, ButtonModule, FileUploadModule],
  templateUrl: './import-project-modal.component.html',
  styleUrl: './import-project-modal.component.scss',
  standalone: true
})
export class ImportProjectModalComponent {
  public targetOrgShort: string = '';
  public selectedFile: File | null = null;
  private readonly _isImporting = signal(false);
  public get isImporting(): boolean { return this._isImporting(); }
  public set isImporting(value: boolean) { this._isImporting.set(value); }
  private readonly _importFailureMessage = signal('');
  public get importFailureMessage(): string { return this._importFailureMessage(); }
  public set importFailureMessage(value: string) { this._importFailureMessage.set(value); }
  private readonly _importFailureReport = signal<ProjectImportFailureReport | null>(null);
  public get importFailureReport(): ProjectImportFailureReport | null { return this._importFailureReport(); }
  public set importFailureReport(value: ProjectImportFailureReport | null) { this._importFailureReport.set(value); }

  public get orgOptions(): { label: string; value: string }[] {
    return this.loginService.myOrgs.map(org => ({ label: org.name, value: org.short }));
  }

  public get chooseFileLabel(): string {
    return this.selectedFile?.name ?? $localize`:@@import-project-choose-file:ZIP-Datei auswählen`;
  }

  constructor(
    private bsModalRef: BsModalRef,
    private router: Router,
    protected loginService: LoginService,
    private projectService: ProjectService,
    private toastr: ToastrService
  ) {
    this.targetOrgShort = this.projectService.searchOrgShort ?? this.loginService.myOrgs[0]?.short ?? '';
  }

  public onFileSelected(event: { currentFiles?: File[] | FileList; files?: File[] | FileList }) {
    const files = event.currentFiles ?? event.files;
    this.selectedFile = (files instanceof FileList ? files[0] : files?.[0]) ?? null;
  }

  public canImport(): boolean {
    return this.selectedFile != null && this.targetOrgShort.trim() !== '' && !this.isImporting;
  }

  public cancel() {
    this.bsModalRef.hide();
  }

  public async import() {
    if (!this.canImport() || !this.selectedFile) return;
    this.isImporting = true;
    this.importFailureMessage = '';
    this.importFailureReport = null;

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('targetOrgShort', this.targetOrgShort);

    try {
      const res: ProjectImportApiResponse = await this.projectService.importProjectFromZip(formData);
      if (res.status === 'ok') {
        this.toastr.success('IMPORTED');
        if (res.secretsBlank) {
          this.toastr.info(
            'PROJECT_SECRETS_MISSING',
            $localize`:@@import-project-secrets-missing:Die Project-Secrets wurden nicht mitübertragen. Trage sie im neuen Projekt manuell ein.`
          );
        }
        await this.projectService.loadProjects();
        this.bsModalRef.hide();
        if (res.projectId) {
          await this.router.navigate(['/bot-admin', 'project', res.projectId]);
        }
      } else {
        this.showImportError(res);
      }
    } catch (error: unknown) {
      const payload = (error as { error?: ProjectImportApiResponse }).error;
      this.showImportError(payload ?? {
        status: 'error',
        message: $localize`:@@import-project-aborted-generic:Der Import wurde abgebrochen. Es wurden keine Änderungen gespeichert.`
      });
    } finally {
      this.isImporting = false;
    }
  }

  private showImportError(response: ProjectImportApiResponse): void {
    const message = response.message ?? $localize`:@@import-project-aborted-generic:Der Import wurde abgebrochen. Es wurden keine Änderungen gespeichert.`;
    this.toastr.error('IMPORTED', message);
    this.importFailureMessage = message;
    this.importFailureReport = response.report ?? null;
  }
}
